import { useState, useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import { LmsUserProfile } from '@rankmi/lms-components'
import '@rankmi/lms-components/dist/index.css'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import { useAccess } from 'contexts/AccessContext'
import { CourseStateType, MonitoringStatus } from 'enum'
import * as fromActions from 'store/actions/participants.action'
import { IMainState } from 'store/states'
import { translations } from './translations'
import { ParticipantHeaderCard } from './ParticipantHeaderCard'

interface IProps {
  userId: number
}

interface CourseActionPayload {
  id: number
  name?: string
}

interface CourseRow {
  id?: number
  course_id?: number
  advance?: number
  course_state?: number | null
}

/**
 * Ficha de participante (admin): header Figma + LmsUserProfile (solo cursos + avance).
 */
export const UserProfile = ({ userId }: IProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const { can, access } = useAccess()
  const [resetCourseId, setResetCourseId] = useState<number | null>(null)
  const [coursesRefreshKey, setCoursesRefreshKey] = useState(0)
  const awaitingCoursesRefresh = useRef(false)
  const participantCourses = useSelector(
    ({ participants }: IMainState) => participants.participantCourses
  )
  const prevParticipantCourses = useRef(participantCourses)

  useEffect(() => {
    if (
      awaitingCoursesRefresh.current &&
      participantCourses !== prevParticipantCourses.current
    ) {
      setCoursesRefreshKey((key) => key + 1)
      awaitingCoursesRefresh.current = false
    }
    prevParticipantCourses.current = participantCourses
  }, [participantCourses])

  const hasCourseStarted = useCallback(
    (course: CourseRow) => course.advance !== MonitoringStatus.Pending,
    []
  )

  const isViewResultsEnabled = useCallback(
    (course: CourseRow) =>
      hasCourseStarted(course) && access.canViewParticipantResults(),
    [access, hasCourseStarted]
  )

  const isResetCourseEnabled = useCallback(
    (course: CourseRow) =>
      hasCourseStarted(course) &&
      can('manageProgress') &&
      Number(course.course_state) !== CourseStateType.Inactive,
    [can, hasCourseStarted]
  )

  const handleViewResults = (course: CourseActionPayload) => {
    history.push(
      `/configuration/user-profile/${userId}/courses/${course.id}/results`
    )
  }

  const handleResetCourse = (course: CourseActionPayload) => {
    setResetCourseId(course.id)
  }

  const confirmReset = () => {
    if (!resetCourseId) return
    awaitingCoursesRefresh.current = true
    dispatch({
      type: fromActions.RESET_COURSE,
      payload: { userId, courseId: resetCourseId },
    })
    setResetCourseId(null)
  }

  return (
    <>
      <ParticipantHeaderCard userId={userId} />
      <LmsUserProfile
        key={`user-profile-${userId}-${coursesRefreshKey}`}
        locale={translations(t)}
        userId={userId}
        showCoursesTab
        showAdvanceStats
        showCertificatesTab={access.canViewParticipantResults()}
        showCourseActions
        isViewResultsEnabled={isViewResultsEnabled}
        isResetCourseEnabled={isResetCourseEnabled}
        onViewResults={handleViewResults}
        onResetCourse={handleResetCourse}
      />
      <ConfirmationModal
        show={!!resetCourseId}
        onClose={() => setResetCourseId(null)}
        onAccept={confirmReset}
        title={t('configuration.user.resetModal.title')}
        text={t('configuration.user.resetModal.text')}
        acceptButtonText={t('configuration.user.resetModal.buttons.accept')}
      />
    </>
  )
}

export default UserProfile
